import { useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Tag, ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { resolveUrl } from "@/lib/api";

const THEMES = [
  { bg: "bg-gradient-to-r from-[#C2185B] to-[#E0457B]", fade: "#D63370", text: "text-[#C2185B]", pill: "bg-[#E8336B]" },
  { bg: "bg-gradient-to-r from-[#1E4FA8] to-[#2F6FD6]", fade: "#2860C0", text: "text-[#1E4FA8]", pill: "bg-[#3B7BE0]" },
  { bg: "bg-gradient-to-r from-[#E8740C] to-[#F59E2A]", fade: "#EF8A1C", text: "text-[#E8740C]", pill: "bg-[#F08A1A]" },
];

export const PromoCard = ({ promo, index, className = "" }) => {
  const navigate = useNavigate();
  const th = THEMES[index % THEMES.length];
  const open = () => { if (promo.business_id) navigate(`/activity/${promo.business_id}`, { state: { tab: "offers", promoId: promo.id } }); else if (promo.link) window.open(promo.link, "_blank"); };
  return (
    <div className={`relative h-[200px] rounded-2xl overflow-hidden shadow-[0_12px_30px_-14px_rgba(15,23,42,0.45)] ${th.bg} ${className}`} data-testid="promo-card">
      {promo.image && (
        <div className="absolute inset-y-0 right-0 w-[58%]">
          <img src={resolveUrl(promo.image)} alt={promo.title} className="h-full w-full object-cover" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(to right, ${th.fade} 0%, ${th.fade}cc 22%, transparent 60%)` }} />
        </div>
      )}
      <div className="relative h-full flex flex-col p-4 sm:p-5 max-w-[78%]">
        <span className={`self-start inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${th.pill} text-white text-[11px] font-extrabold tracking-wide shadow`}><Tag size={12} /> ΠΡΟΣΦΟΡΑ</span>
        <h4 className="mt-2.5 font-display font-extrabold text-white text-lg leading-tight line-clamp-2 drop-shadow">{promo.title}</h4>
        {promo.description && <p className="mt-1 text-white/90 text-sm leading-snug line-clamp-2 drop-shadow">{promo.description}</p>}
        {(promo.business_id || promo.link) && <button type="button" onClick={open} data-testid="promo-card-btn" className={`mt-auto self-start inline-flex items-center gap-3 h-9 pl-4 pr-3 rounded-full bg-white ${th.text} font-bold text-[13px] whitespace-nowrap shadow hover:gap-4 transition-[gap]`}>
          Δες την προσφορά <ArrowRight size={16} />
        </button>}
      </div>
    </div>
  );
};

export const PromoCards = ({ promotions = [] }) => {
  const track = useRef(null);
  if (!promotions.length) return null;
  const scroll = (d) => {
    const el = track.current;
    const card = el?.firstElementChild;
    if (el && card) el.scrollBy({ left: d * (card.offsetWidth + 16), behavior: "smooth" });
  };
  return (
    <section data-testid="home-promotions">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-xl sm:text-2xl font-extrabold text-[#0B2454]">Προσφορές & ευκαιρίες</h3>
        <div className="flex items-center gap-2">
          {promotions.length > 2 && (
            <div className={`items-center gap-2 ${promotions.length > 3 ? "flex" : "flex 2xl:hidden"}`}>
              <button onClick={() => scroll(-1)} data-testid="promo-prev" aria-label="Προηγούμενη" className="h-8 w-8 grid place-items-center rounded-full bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"><ChevronLeft size={16} /></button>
              <button onClick={() => scroll(1)} data-testid="promo-next" aria-label="Επόμενη" className="h-8 w-8 grid place-items-center rounded-full bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"><ChevronRight size={16} /></button>
            </div>
          )}
          <Link to="/offers" data-testid="promo-see-all" className="ml-2 whitespace-nowrap text-sm font-bold text-primary inline-flex items-center gap-1 hover:gap-2 transition-[gap]">Δες όλες <ArrowRight size={16} /></Link>
        </div>
      </div>
      <div ref={track} className="flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar" data-testid="promo-track">
        {promotions.map((p, i) => (
          <PromoCard key={p.id} promo={p} index={i} className="snap-start shrink-0 w-full sm:w-[calc((100%-16px)/2)] lg:w-[calc((100%-16px)/2)] 2xl:w-[calc((100%-32px)/3)]" />
        ))}
      </div>
    </section>
  );
};
